import { useMutation } from "@tanstack/react-query";
import {
  CopyIcon,
  Divider,
  GKButton,
  GKTextField,
  Grid2,
  IconButton,
  Typography,
} from "components";
import { useFetchQuery } from "hooks/useQueries";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useAppSelector } from "store/store";
import axiosInstance from "utils/axiosInstance";
import { ThemeContainer } from "utils/Theme";

type TelegramFormValues = {
  name: string;
  botToken: string;
  telegramId: string;
  firstName: string;
  botLink: string;
};

const TelegramSetup = () => {
  const { theme } = ThemeContainer();

  const userData = useAppSelector((state) => state?.user?.userData);

  const form = useForm<TelegramFormValues>({
    defaultValues: {
      name: "",
      botToken: "",
      telegramId: "",
      firstName: "",
      botLink: "",
    },
  });

  useFetchQuery({
    key: ["TELEGRAM_DETAILS"],
    route: "/telegram-details/",
    success: (res: any) => {
      form.reset({ ...res?.data?.data });
    },
  });

  const { mutate, isLoading } = useMutation({
    mutationFn: (requestedData: TelegramFormValues) =>
      axiosInstance.patch(
        `telegram-details/${userData.companyId}/`,
        requestedData,
      ),
    onSuccess: (response) => {
      toast.success(response.data?.message || "Action completed successfully.");
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message ||
          "Something went wrong, please try again.",
      );
    },
  });

  return (
    <form
      onSubmit={form.handleSubmit((values) => {
        mutate(values);
      })}
    >
      <Grid2 container spacing={2}>
        <Grid2 size={3}>
          <Typography
            fontSize={16}
            fontWeight={500}
            color={theme.palette.secondaryText.main}
          >
            Setup
          </Typography>
        </Grid2>
        <Grid2
          container
          size={9}
          spacing={2}
          display={"flex"}
          alignItems={"center"}
        >
          <Grid2 size={5}>
            <Controller
              control={form.control}
              name="name"
              rules={{
                required: {
                  value: true,
                  message: "Please enter name",
                },
              }}
              render={({ field, fieldState: { error } }) => {
                return (
                  <GKTextField
                    {...field}
                    requiredField
                    inputLabel={"Bot Name"}
                    error={!!error}
                    helperText={!!error && error.message}
                  />
                );
              }}
            />
          </Grid2>
          <Grid2 size={5}>
            <Controller
              control={form.control}
              name="botToken"
              rules={{
                required: {
                  value: true,
                  message: "Please enter botToken",
                },
              }}
              render={({ field, fieldState: { error } }) => {
                return (
                  <GKTextField
                    {...field}
                    requiredField
                    inputLabel={"Bot Token"}
                    error={!!error}
                    helperText={!!error && error.message}
                  />
                );
              }}
            />
          </Grid2>
          <Grid2 size={5}>
            <Controller
              control={form.control}
              name="telegramId"
              rules={{
                required: {
                  value: true,
                  message: "Please enter telegramId",
                },
              }}
              render={({ field, fieldState: { error } }) => {
                return (
                  <GKTextField
                    {...field}
                    requiredField
                    inputLabel={"Bot Token No."}
                    error={!!error}
                    helperText={!!error && error.message}
                  />
                );
              }}
            />
          </Grid2>
          <Grid2 size={5}>
            <Controller
              control={form.control}
              name="firstName"
              rules={{
                required: {
                  value: true,
                  message: "Please enter firstName",
                },
              }}
              render={({ field, fieldState: { error } }) => {
                return (
                  <GKTextField
                    {...field}
                    requiredField
                    inputLabel={"User Name"}
                    error={!!error}
                    helperText={!!error && error.message}
                  />
                );
              }}
            />
          </Grid2>
          <Grid2 size={5}>
            <Controller
              control={form.control}
              name="botLink"
              rules={{
                required: {
                  value: true,
                  message: "Please enter Bot Link",
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <GKTextField
                  {...field}
                  requiredField
                  inputLabel="Bot Link"
                  error={!!error}
                  helperText={!!error && error.message}
                  slotProps={{
                    input: {
                      style: {
                        paddingRight: 12,
                      },
                      endAdornment: (
                        <IconButton
                          onClick={() => {
                            if (field.value) {
                              navigator.clipboard.writeText(field.value);
                              toast.success("Copied to clipboard!");
                            } else {
                              toast.error("No link to copy!");
                            }
                          }}
                          style={{
                            padding: 0,
                          }}
                        >
                          <CopyIcon />
                        </IconButton>
                      ),
                    },
                  }}
                />
              )}
            />
          </Grid2>

          <Grid2
            size="grow"
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            mt={2.5}
          >
            <GKButton loading={isLoading} variant="contained" type="submit">
              Save
            </GKButton>
          </Grid2>
        </Grid2>
        <Grid2 size={12}>
          <Divider />
        </Grid2>
      </Grid2>
    </form>
  );
};

export default TelegramSetup;
